import React, { useState, useEffect, useRef } from 'react';
import { Phone, MapPin, Clock, ChefHat, Heart, Award, Star, Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import axios from 'axios';
import Lightbox from '../components/Lightbox';
import { useLightbox } from '../hooks/useLightbox';
import { API } from '../utils/api';

const DEFAULT_HOURS_SUMMARY = "Mar-Ven 9h-18h30 • Sam 9h-17h30";

const formatHoursForHeader = (hours) => {
  if (!hours || !hours.length) return DEFAULT_HOURS_SUMMARY;
  const abbrMap = {
    "Lundi": "Lun", "Mardi": "Mar", "Mercredi": "Mer",
    "Jeudi": "Jeu", "Vendredi": "Ven", "Samedi": "Sam", "Dimanche": "Dim"
  };
  const fmt = (t) => t ? t.replace(':30', 'h30').replace(':00', 'h') : '';
  const groups = [];
  let cur = null;
  hours.forEach(h => {
    if (!h.isOpen) { cur = null; return; }
    const key = `${h.open}|${h.close}`;
    if (cur && cur.key === key) {
      cur.days.push(abbrMap[h.day] || h.day.substring(0, 3));
    } else {
      cur = { key, days: [abbrMap[h.day] || h.day.substring(0, 3)], open: h.open, close: h.close };
      groups.push(cur);
    }
  });
  if (!groups.length) return "Fermé";
  return groups.map(g => {
    const ds = g.days.length > 1 ? `${g.days[0]}-${g.days[g.days.length - 1]}` : g.days[0];
    return `${ds} ${fmt(g.open)}-${fmt(g.close)}`;
  }).join(' • ');
};

const computeOpenStatus = (hours) => {
  if (!hours || !hours.length) return { isOpen: null, detail: '' };

  const now = new Date();
  const dayNames = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
  const todayName = dayNames[now.getDay()];
  const todayHours = hours.find(h => h.day === todayName);
  const fmt = (t) => t ? t.replace(':30', 'h30').replace(':00', 'h') : '';

  const formatCountdown = (totalMinutes) => {
    if (totalMinutes <= 0) return '';
    if (totalMinutes < 60) return `dans ${totalMinutes} min`;
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `dans ${h}h${m > 0 ? String(m).padStart(2, '0') : ''}`;
  };

  const findNextOpen = (fromDayName, afterMinutes = 0) => {
    const order = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
    const idx = order.indexOf(fromDayName);
    for (let i = 1; i <= 7; i++) {
      const nextName = order[(idx + i) % 7];
      const nextDay = hours.find(h => h.day === nextName);
      if (nextDay && nextDay.isOpen && nextDay.open) {
        const [nh, nm] = nextDay.open.split(':').map(Number);
        const minsUntil = afterMinutes + i * 24 * 60 - (now.getHours() * 60 + now.getMinutes()) + nh * 60 + nm;
        const countdown = formatCountdown(minsUntil);
        const label = i === 1 ? 'demain' : nextName.toLowerCase();
        return countdown
          ? `Ouvre ${countdown}`
          : `Ouvre ${label} à ${fmt(nextDay.open)}`;
      }
    }
    return '';
  };

  if (!todayHours || !todayHours.isOpen || !todayHours.open || !todayHours.close) {
    return { isOpen: false, detail: findNextOpen(todayName) };
  }

  const [openH, openM] = todayHours.open.split(':').map(Number);
  const [closeH, closeM] = todayHours.close.split(':').map(Number);
  const curMin = now.getHours() * 60 + now.getMinutes();
  const openMin = openH * 60 + openM;
  const closeMin = closeH * 60 + closeM;

  if (curMin >= openMin && curMin < closeMin) {
    // Vérifier si pendant la coupure
    if (todayHours.hasBreak && todayHours.breakStart && todayHours.breakEnd) {
      const [bs, bsm] = todayHours.breakStart.split(':').map(Number);
      const [be, bem] = todayHours.breakEnd.split(':').map(Number);
      const breakStartMin = bs * 60 + bsm;
      const breakEndMin = be * 60 + bem;
      if (curMin >= breakStartMin && curMin < breakEndMin) {
        const minsUntil = breakEndMin - curMin;
        return { isOpen: false, detail: `Ouvre ${formatCountdown(minsUntil)}` };
      }
      if (curMin < breakStartMin) {
        return { isOpen: true, detail: `Ferme à ${fmt(todayHours.breakStart)}` };
      }
    }
    return { isOpen: true, detail: `Ferme à ${fmt(todayHours.close)}` };
  } else if (curMin < openMin) {
    const minsUntil = openMin - curMin;
    return { isOpen: false, detail: `Ouvre ${formatCountdown(minsUntil)}` };
  } else {
    return { isOpen: false, detail: findNextOpen(todayName) };
  }
};

// Composant galerie d'images pour les modals
const ModalGallery = ({ item }) => {  const [idx, setIdx] = useState(0);
  const images = (item.images && item.images.length > 0)
    ? item.images
    : (item.image ? [item.image] : []);

  if (images.length === 0) return null;

  return (
    <div className="relative w-full rounded-lg overflow-hidden bg-gray-50">
      <img
        src={images[idx]}
        alt={`Image ${idx + 1}`}
        className="w-full h-auto max-h-[500px] object-contain"
      />
      {images.length > 1 && (
        <>
          <button
            onClick={() => setIdx(i => Math.max(0, i - 1))}
            disabled={idx === 0}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 disabled:opacity-30 text-white rounded-full p-2 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => setIdx(i => Math.min(images.length - 1, i + 1))}
            disabled={idx === images.length - 1}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 disabled:opacity-30 text-white rounded-full p-2 transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`w-2 h-2 rounded-full transition-all ${i === idx ? 'bg-white scale-125' : 'bg-white/60 hover:bg-white/80'}`}
              />
            ))}
          </div>
          <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
            {idx + 1} / {images.length}
          </div>
        </>
      )}
    </div>
  );
};

const Home = () => {
  const [actualites, setActualites] = useState([]);
  const [decouvrir, setDecouvrir] = useState([]);
  const [patesProduits, setPatesProduits] = useState([]);
  const [fromagesProduits, setFromagesProduits] = useState([]);
  const [charcuteriesProduits, setCharcuteriesProduits] = useState([]);
  const [platsProduits, setPlatsProduits] = useState([]);
  const [dessertsProduits, setDessertsProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDecouvrir, setLoadingDecouvrir] = useState(true);
  const [hoursSummary, setHoursSummary] = useState(DEFAULT_HOURS_SUMMARY);
  const [openStatus, setOpenStatus] = useState({ isOpen: null, detail: '' });
  const [hoursData, setHoursData] = useState([]);
  const lightbox = useLightbox();
  
  // États pour les modales
  const [showActualiteModal, setShowActualiteModal] = useState(false);
  const [selectedActualite, setSelectedActualite] = useState(null);
  const [showDecouvrirModal, setShowDecouvrirModal] = useState(false);
  const [selectedDecouvrir, setSelectedDecouvrir] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Refs pour le scroll
  const actualitesScrollRef = useRef(null);
  const decouvrirScrollRef = useRef(null);
  const patesScrollRef = useRef(null);
  const fromagesScrollRef = useRef(null);
  const charcuteriesScrollRef = useRef(null);
  const platsScrollRef = useRef(null);
  const dessertsScrollRef = useRef(null);

  useEffect(() => {
    fetchActualites();
    fetchDecouvrir();
    fetchProduitsByCategory();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/settings`);
      if (response.ok) {
        const data = await response.json();
        if (data.hours) {
          setHoursData(data.hours);
          setHoursSummary(formatHoursForHeader(data.hours));
          setOpenStatus(computeOpenStatus(data.hours));
        }
      }
    } catch {
      // Keep fallback
    }
  };

  // Rafraîchir le countdown toutes les minutes sans re-fetch API
  useEffect(() => {
    if (hoursData.length === 0) return;
    const interval = setInterval(() => {
      setOpenStatus(computeOpenStatus(hoursData));
    }, 60000);
    return () => clearInterval(interval);
  }, [hoursData]);

  const fetchActualites = async () => {
    try {
      const response = await axios.get(`${API}/actualites`);
      setActualites(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching actualites:', error);
      setLoading(false);
    }
  };

  const fetchDecouvrir = async () => {
    try {
      const response = await axios.get(`${API}/decouvrir`);
      setDecouvrir(response.data);
      setLoadingDecouvrir(false);
    } catch (error) {
      console.error('Error fetching découvrir:', error);
      setLoadingDecouvrir(false);
    }
  };

  const fetchProduitsByCategory = async () => {
    try {
      // Fetch pâtes
      const patesResponse = await axios.get(`${API}/products?category=pates`);
      setPatesProduits(patesResponse.data);

      // Fetch fromages
      const fromagesResponse = await axios.get(`${API}/products?category=fromages`);
      setFromagesProduits(fromagesResponse.data);

      // Fetch charcuteries
      const charcuteriesResponse = await axios.get(`${API}/products?category=charcuteries`);
      setCharcuteriesProduits(charcuteriesResponse.data);

      // Fetch plats cuisinés
      const platsResponse = await axios.get(`${API}/products?category=plats`);
      setPlatsProduits(platsResponse.data);

      // Fetch desserts
      const dessertsResponse = await axios.get(`${API}/products?category=desserts`);
      setDessertsProduits(dessertsResponse.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleCall = () => {
    window.location.href = 'tel:+33978802623';
  };

  const handleDirections = () => {
    window.open('https://www.google.com/maps/search/?api=1&query=1+Place+du+18+Novembre+1944,+90600+Grandvillars', '_blank');
  };

  // Fonctions de navigation pour le scroll
  const scrollLeft = (ref) => {
    if (ref.current) {
      ref.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollRight = (ref) => {
    if (ref.current) {
      ref.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  const getCategoryBadge = (category) => {
    switch (category) {
      case 'nouveaute':
        return { label: '✨ Nouveauté', class: 'bg-green-100 text-green-700' };
      case 'info':
        return { label: '📢 Info', class: 'bg-blue-100 text-blue-700' };
      case 'promotion':
        return { label: '🎉 Promotion', class: 'bg-orange-100 text-orange-700' };
      default:
        return { label: '📢 Info', class: 'bg-blue-100 text-blue-700' };
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <main className="min-h-screen">
      <div className="pt-20">
        {/* Hero Section */}
        <section className="relative italian-section-bg py-28 overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=1920&h=1080&fit=crop&crop=center')] bg-cover bg-center opacity-5"></div>
          <div className="container mx-auto relative">
            <div className="max-w-4xl mx-auto">
              <div className="text-center">
                <h1 className="heading-hero mb-6 animate-fade-in-text animation-delay-0">
                  Épicerie fine italienne & pâtes fraîches maison
                  <span className="block mt-2" style={{ color: '#C41E3A' }}>IL TRULLO</span>
                </h1>
                <p className="text-lead mb-8 text-gray-700 animate-fade-in-text animation-delay-150">
                  Bienvenue dans notre boutique italienne, un lieu où se rencontrent gourmandise et convivialité. Savourez nos créations maison préparées avec soin et passion, et venez découvrir parmi tous les produits que nous faisons venir d'Italie, à la fois les classiques des grandes marques italiennes les plus célèbres mais également de véritables trésors de petits producteurs et artisans que nous avons choisis avec grand soin pour vous offrir le meilleur de l'Italie.
                </p>
                <p className="text-body mb-8 text-gray-600 animate-fade-in-text animation-delay-300">
                  Passez nous voir au cœur de Grandvillars pour un vrai voyage culinaire.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-text animation-delay-450">
                  <button onClick={handleCall} className="btn btn-secondary btn-lg">
                    <Phone className="w-5 h-5" />
                    Appeler la boutique
                  </button>
                  <button onClick={handleDirections} className="btn btn-primary btn-lg">
                    <MapPin className="w-5 h-5" />
                    Voir l'itinéraire
                  </button>
                </div>
                <div className="flex flex-col sm:flex-row gap-6 mt-8 text-sm justify-center animate-fade-in-text animation-delay-600">
                  <div className="flex items-center gap-2">
                    {openStatus.isOpen === true && (
                      <>
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse flex-shrink-0"></span>
                        <span className="font-semibold text-green-700">Ouvert</span>
                        {openStatus.detail && <span className="text-gray-500">• {openStatus.detail}</span>}
                      </>
                    )}
                    {openStatus.isOpen === false && (
                      <>
                        <span className="inline-block w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                        <span className="font-semibold text-red-600">Fermé</span>
                        {openStatus.detail && <span className="text-gray-500">• {openStatus.detail}</span>}
                      </>
                    )}
                    {openStatus.isOpen === null && (
                      <>
                        <Clock className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span className="font-medium text-gray-600">{hoursSummary}</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="w-4 h-4 text-red-600" />
                    <span>Grandvillars, Territoire de Belfort</span>
                  </div>
                </div>

                {/* Tableau horaires compact — visible uniquement quand fermé */}
                {openStatus.isOpen === false && hoursData.length > 0 && (() => {
                  const fmt = (t) => t ? t.replace(':30', 'h30').replace(':00', 'h') : '';
                  const abbrMap = { "Lundi": "Lun", "Mardi": "Mar", "Mercredi": "Mer", "Jeudi": "Jeu", "Vendredi": "Ven", "Samedi": "Sam", "Dimanche": "Dim" };
                  const groups = [];
                  let cur = null;
                  hoursData.forEach(h => {
                    if (!h.isOpen) { cur = null; return; }
                    const key = `${h.open}|${h.close}`;
                    if (cur && cur.key === key) {
                      cur.days.push(abbrMap[h.day] || h.day);
                    } else {
                      cur = { key, days: [abbrMap[h.day] || h.day], open: h.open, close: h.close };
                      groups.push(cur);
                    }
                  });
                  if (groups.length === 0) return null;
                  return (
                    <div className="mt-5 inline-block animate-fade-in-text animation-delay-600">
                      <div className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-xl px-6 py-4 shadow-sm text-left">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                          <Clock className="w-3 h-3" />
                          Horaires d'ouverture
                        </p>
                        <div className="space-y-1.5">
                          {groups.map((g, i) => (
                            <div key={i} className="flex items-center justify-between gap-8 text-sm">
                              <span className="text-gray-700 font-medium">
                                {g.days.length > 2
                                  ? `${g.days[0]} – ${g.days[g.days.length - 1]}`
                                  : g.days.join(', ')}
                              </span>
                              <span className="text-gray-600 font-semibold tabular-nums">
                                {fmt(g.open)} – {fmt(g.close)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </section>

        {/* Actualités Section */}
        <section className="section italian-section-alt">
          <div className="italian-divider mb-12"></div>
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="heading-lg mb-4 text-gray-900">Actualités</h2>
              <p className="text-lead text-gray-700 max-w-2xl mx-auto">
                Découvrez nos dernières nouveautés, promotions et événements
              </p>
            </div>
            <div className="relative">
              {/* Boutons de navigation pour Actualités */}
              {!loading && actualites.length > 1 && (
                <>
                  <button
                    onClick={() => scrollLeft(actualitesScrollRef)}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-300 hover:scale-110"
                    aria-label="Précédent"
                  >
                    <ChevronLeft className="w-6 h-6 text-gray-700" />
                  </button>
                  <button
                    onClick={() => scrollRight(actualitesScrollRef)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-300 hover:scale-110"
                    aria-label="Suivant"
                  >
                    <ChevronRight className="w-6 h-6 text-gray-700" />
                  </button>
                </>
              )}
              
              {loading ? (
                <div className="text-center py-12">
                  <p className="text-gray-600">Chargement des actualités...</p>
                </div>
              ) : actualites.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600">Aucune actualité disponible pour le moment.</p>
                </div>
              ) : (
                <div ref={actualitesScrollRef} className="flex gap-6 overflow-x-auto scroll-smooth pb-4 px-2 actualites-scroll-container">
                  {actualites.map((actualite) => {
                    const badge = getCategoryBadge(actualite.category);
                    return (
                      <div
                        key={actualite.id}
                        className={`flex-shrink-0 w-[350px] bg-white rounded-xl shadow-lg overflow-hidden card-hover-elegant cursor-pointer ${
                          actualite.isNew ? 'ring-2 ring-orange-400' : ''
                        }`}
                        onClick={() => {
                          setSelectedActualite(actualite);
                          setShowActualiteModal(true);
                        }}
                      >
                        <div className="relative h-48 overflow-hidden">
                          <img
                            src={actualite.image}
                            alt={actualite.title}
                            className="w-full h-full object-cover"
                          />
                          {actualite.isNew && (
                            <div className="absolute top-3 right-3 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                              Nouveau
                            </div>
                          )}
                        </div>
                        <div className="p-6">
                          <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(actualite.date)}</span>
                          </div>
                          <h3 className="heading-sm text-gray-900 mb-3 line-clamp-2">{actualite.title}</h3>
                          <p className="text-body text-gray-600 line-clamp-3 mb-4">{actualite.description}</p>
                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.class}`}>
                              {badge.label}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Découvrir Section */}
        <section className="section italian-section-bg">
          <div className="italian-divider mb-12"></div>
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="heading-lg mb-4 text-gray-900">Découvrir</h2>
              <p className="text-lead text-gray-700 max-w-2xl mx-auto">
                Explorez nos produits et découvertes italiennes
              </p>
            </div>
            <div className="relative">
              {/* Boutons de navigation pour Découvrir */}
              {!loadingDecouvrir && decouvrir.length > 1 && (
                <>
                  <button
                    onClick={() => scrollLeft(decouvrirScrollRef)}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-300 hover:scale-110"
                    aria-label="Précédent"
                  >
                    <ChevronLeft className="w-6 h-6 text-gray-700" />
                  </button>
                  <button
                    onClick={() => scrollRight(decouvrirScrollRef)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-300 hover:scale-110"
                    aria-label="Suivant"
                  >
                    <ChevronRight className="w-6 h-6 text-gray-700" />
                  </button>
                </>
              )}
              
              {loadingDecouvrir ? (
                <div className="text-center py-12">
                  <p className="text-gray-600">Chargement...</p>
                </div>
              ) : decouvrir.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600">Aucun élément disponible pour le moment.</p>
                </div>
              ) : (
                <div ref={decouvrirScrollRef} className="flex gap-6 overflow-x-auto scroll-smooth pb-4 px-2 actualites-scroll-container">
                  {decouvrir.map((item) => (
                    <div
                      key={item.id}
                      className="flex-shrink-0 w-[350px] bg-white rounded-xl shadow-lg overflow-hidden card-hover-elegant cursor-pointer"
                      onClick={() => {
                        setSelectedDecouvrir(item);
                        setShowDecouvrirModal(true);
                      }}
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(item.date)}</span>
                          </div>
                          {item.category && (
                            <span className="text-xs font-medium px-2 py-1 bg-green-100 text-green-700 rounded">
                              {item.category}
                            </span>
                          )}
                        </div>
                        <h3 className="heading-sm text-gray-900 mb-3 line-clamp-2">{item.title}</h3>
                        <p className="text-body text-gray-600 line-clamp-3 mb-4">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Carrousel Pâtes Fraîches */}
        <section className="section italian-section-bg">
          <div className="container mx-auto">
            <div className="text-center mb-8">
              <h2 className="heading-lg mb-2 text-gray-900">Nos pâtes fraîches</h2>
              <p className="text-body text-gray-600">Faites maison avec passion</p>
            </div>
            {patesProduits.length > 0 ? (
              <div className="relative">
                <button
                  onClick={() => scrollLeft(patesScrollRef)}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all"
                >
                  <ChevronLeft className="w-6 h-6 text-gray-700" />
                </button>
                <button
                  onClick={() => scrollRight(patesScrollRef)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all"
                >
                  <ChevronRight className="w-6 h-6 text-gray-700" />
                </button>
                <div ref={patesScrollRef} className="flex gap-6 overflow-x-auto scroll-smooth pb-4 px-2 actualites-scroll-container">
                  {patesProduits.map((product) => (
                    <div 
                      key={product.id} 
                      className="flex-shrink-0 w-[350px] bg-white rounded-xl shadow-lg overflow-hidden card-hover-elegant cursor-pointer"
                      onClick={() => {
                        setSelectedProduct(product);
                        setShowProductModal(true);
                      }}
                    >
                      <img 
                        src={product.image} 
                        alt={product.title} 
                        className="w-full h-48 object-cover" 
                      />
                      <div className="p-6">
                        <h3 className="heading-sm mb-3 text-gray-900">{product.title}</h3>
                        <p className="text-body text-gray-600 line-clamp-2 mb-4">{product.description}</p>
                        <div className="text-sm text-green-700 font-medium">{product.cookingTimeOrAdvice}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">Produits bientôt disponibles. Passez en boutique pour découvrir notre gamme complète !</p>
              </div>
            )}
          </div>
        </section>

        {/* Carrousel Fromages */}
        <section className="section italian-section-alt">
          <div className="container mx-auto">
            <div className="text-center mb-8">
              <h2 className="heading-lg mb-2 text-gray-900">Nos fromages</h2>
              <p className="text-body text-gray-600">Sélection artisanale italienne</p>
            </div>
            {fromagesProduits.length > 0 ? (
              <div className="relative">
                <button onClick={() => scrollLeft(fromagesScrollRef)} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all">
                  <ChevronLeft className="w-6 h-6 text-gray-700" />
                </button>
                <button onClick={() => scrollRight(fromagesScrollRef)} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all">
                  <ChevronRight className="w-6 h-6 text-gray-700" />
                </button>
                <div ref={fromagesScrollRef} className="flex gap-6 overflow-x-auto scroll-smooth pb-4 px-2 actualites-scroll-container">
                  {fromagesProduits.map((product) => (
                    <div 
                      key={product.id} 
                      className="flex-shrink-0 w-[350px] bg-white rounded-xl shadow-lg overflow-hidden card-hover-elegant cursor-pointer"
                      onClick={() => {
                        setSelectedProduct(product);
                        setShowProductModal(true);
                      }}
                    >
                      <img 
                        src={product.image} 
                        alt={product.title} 
                        className="w-full h-48 object-cover" 
                      />
                      <div className="p-6">
                        <h3 className="heading-sm mb-3 text-gray-900">{product.title}</h3>
                        <p className="text-body text-gray-600 line-clamp-2 mb-4">{product.description}</p>
                        <div className="text-sm text-green-700 font-medium">{product.cookingTimeOrAdvice}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">Produits bientôt disponibles. Passez en boutique pour découvrir notre gamme complète !</p>
              </div>
            )}
          </div>
        </section>

        {/* Carrousel Charcuteries */}
        {charcuteriesProduits.length > 0 && (
          <section className="section italian-section-bg">
            <div className="container mx-auto">
              <div className="text-center mb-8">
                <h2 className="heading-lg mb-2 text-gray-900">Nos charcuteries</h2>
                <p className="text-body text-gray-600">Mortadelle, San Daniele, Parme grand affinage...</p>
              </div>
              <div className="relative">
                <button onClick={() => scrollLeft(charcuteriesScrollRef)} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all">
                  <ChevronLeft className="w-6 h-6 text-gray-700" />
                </button>
                <button onClick={() => scrollRight(charcuteriesScrollRef)} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all">
                  <ChevronRight className="w-6 h-6 text-gray-700" />
                </button>
                <div ref={charcuteriesScrollRef} className="flex gap-6 overflow-x-auto scroll-smooth pb-4 px-2 actualites-scroll-container">
                  {charcuteriesProduits.map((product) => (
                    <div 
                      key={product.id} 
                      className="flex-shrink-0 w-[350px] bg-white rounded-xl shadow-lg overflow-hidden card-hover-elegant cursor-pointer"
                      onClick={() => {
                        setSelectedProduct(product);
                        setShowProductModal(true);
                      }}
                    >
                      <img 
                        src={product.image} 
                        alt={product.title} 
                        className="w-full h-48 object-cover" 
                      />
                      <div className="p-6">
                        <h3 className="heading-sm mb-3 text-gray-900">{product.title}</h3>
                        <p className="text-body text-gray-600 line-clamp-2 mb-4">{product.description}</p>
                        <div className="text-sm text-green-700 font-medium">{product.cookingTimeOrAdvice}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Carrousel Plats Cuisinés */}
        {platsProduits.length > 0 && (
          <section className="section italian-section-alt">
            <div className="container mx-auto">
              <div className="text-center mb-8">
                <h2 className="heading-lg mb-2 text-gray-900">Nos plats cuisinés</h2>
                <p className="text-body text-gray-600">Prêts à réchauffer</p>
              </div>
              <div className="relative">
                <button onClick={() => scrollLeft(platsScrollRef)} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all">
                  <ChevronLeft className="w-6 h-6 text-gray-700" />
                </button>
                <button onClick={() => scrollRight(platsScrollRef)} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all">
                  <ChevronRight className="w-6 h-6 text-gray-700" />
                </button>
                <div ref={platsScrollRef} className="flex gap-6 overflow-x-auto scroll-smooth pb-4 px-2 actualites-scroll-container">
                  {platsProduits.map((product) => (
                    <div 
                      key={product.id} 
                      className="flex-shrink-0 w-[350px] bg-white rounded-xl shadow-lg overflow-hidden card-hover-elegant cursor-pointer"
                      onClick={() => {
                        setSelectedProduct(product);
                        setShowProductModal(true);
                      }}
                    >
                      <img 
                        src={product.image} 
                        alt={product.title} 
                        className="w-full h-48 object-cover" 
                      />
                      <div className="p-6">
                        <h3 className="heading-sm mb-3 text-gray-900">{product.title}</h3>
                        <p className="text-body text-gray-600 line-clamp-2 mb-4">{product.description}</p>
                        <div className="text-sm text-green-700 font-medium">{product.cookingTimeOrAdvice}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Carrousel Desserts/Classiques */}
        <section className="section italian-section-bg">
          <div className="container mx-auto">
            <div className="text-center mb-8">
              <h2 className="heading-lg mb-2 text-gray-900">Les grands classiques Italiens</h2>
            </div>
            {dessertsProduits.length > 0 ? (
              <div className="relative">
                <button onClick={() => scrollLeft(dessertsScrollRef)} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all">
                  <ChevronLeft className="w-6 h-6 text-gray-700" />
                </button>
                <button onClick={() => scrollRight(dessertsScrollRef)} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all">
                  <ChevronRight className="w-6 h-6 text-gray-700" />
                </button>
                <div ref={dessertsScrollRef} className="flex gap-6 overflow-x-auto scroll-smooth pb-4 px-2 actualites-scroll-container">
                  {dessertsProduits.map((product) => (
                    <div 
                      key={product.id} 
                      className="flex-shrink-0 w-[350px] bg-white rounded-xl shadow-lg overflow-hidden card-hover-elegant cursor-pointer"
                      onClick={() => {
                        setSelectedProduct(product);
                        setShowProductModal(true);
                      }}
                    >
                      <img 
                        src={product.image} 
                        alt={product.title} 
                        className="w-full h-48 object-cover" 
                      />
                      <div className="p-6">
                        <h3 className="heading-sm mb-3 text-gray-900">{product.title}</h3>
                        <p className="text-body text-gray-600 line-clamp-2 mb-4">{product.description}</p>
                        <div className="text-sm text-green-700 font-medium">{product.cookingTimeOrAdvice}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">Produits bientôt disponibles. Passez en boutique pour découvrir notre gamme complète !</p>
              </div>
            )}
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="section italian-section-alt">
          <div className="italian-divider mb-12"></div>
          <div className="container mx-auto transition-all duration-700 opacity-100 translate-y-0">
            <div className="text-center mb-12">
              <h2 className="heading-lg mb-4">Pourquoi choisir IL TRULLO ?</h2>
              <p className="text-lead text-gray-600 max-w-2xl mx-auto">
                Découvrez l'authenticité italienne à travers nos produits soigneusement sélectionnés et nos pâtes fraîches préparées chaque semaine.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center p-6 rounded-lg bg-green-50 card-hover-elegant">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ChefHat className="w-8 h-8 text-white" />
                </div>
                <h3 className="heading-sm mb-3 text-green-800">Atelier sur place</h3>
                <p className="text-body text-gray-600">
                  Pâtes fraîches fabriquées chaque semaine dans notre atelier avec des ingrédients de qualité.
                </p>
              </div>
              <div className="text-center p-6 rounded-lg bg-red-50 card-hover-elegant">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <h3 className="heading-sm mb-3 text-red-800">Produits authentiques</h3>
                <p className="text-body text-gray-600">
                  Sélection rigoureuse de produits italiens traditionnels directement importés de leurs régions d'origine.
                </p>
              </div>
              <div className="text-center p-6 rounded-lg bg-yellow-50 card-hover-elegant">
                <div className="w-16 h-16 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <h3 className="heading-sm mb-3 text-yellow-800">Savoir-faire artisanal</h3>
                <p className="text-body text-gray-600">
                  Préparations maison pour nos pâtes fraîches, lasagnes, pizza, focaccia e tutti quanti selon les recettes traditionnelles.
                </p>
              </div>
              <div className="text-center p-6 rounded-lg bg-blue-50 card-hover-elegant">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-white" />
                </div>
                <h3 className="heading-sm mb-3 text-blue-800">Conseils personnalisés</h3>
                <p className="text-body text-gray-600">
                  Notre équipe vous conseille sur la cuisson, les accords et vous fait découvrir de nouvelles saveurs.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section bg-gradient-to-r from-green-700 to-green-600 text-white">
          <div className="container mx-auto text-center transition-all duration-700 opacity-100 translate-y-0">
            <h2 className="heading-lg mb-4">Venez découvrir IL TRULLO</h2>
            <p className="text-lead mb-8 opacity-90">
              Notre équipe vous attend pour vous faire découvrir l'authenticité de la cuisine italienne
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={handleCall} className="btn btn-secondary btn-lg">
                <Phone className="w-5 h-5" />
                +33 9 78 80 26 23
              </button>
              <button onClick={handleDirections} className="btn bg-white text-green-700 hover:bg-gray-100 btn-lg">
                <MapPin className="w-5 h-5" />
                1 Place du 18 Novembre 1944
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Modal Actualité */}
      {showActualiteModal && selectedActualite && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100000] p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
              <h3 className="text-2xl font-bold text-gray-900">Actualité</h3>
              <button
                onClick={() => {
                  setShowActualiteModal(false);
                  setSelectedActualite(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-8 h-8" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Vidéo si présente */}
              {selectedActualite.video && (
                <div className="w-full rounded-lg overflow-hidden">
                  {selectedActualite.video.includes('youtube.com') || selectedActualite.video.includes('youtu.be') ? (
                    <iframe
                      src={selectedActualite.video.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                      title={selectedActualite.title}
                      className="w-full h-[400px]"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <video
                      src={selectedActualite.video}
                      controls
                      className="w-full h-auto max-h-[400px] bg-black"
                    />
                  )}
                </div>
              )}

              {/* Galerie d'images */}
              <ModalGallery key={selectedActualite.id} item={selectedActualite} />

              {/* Titre */}
              <div>
                <h4 className="text-3xl font-bold text-gray-900">{selectedActualite.title}</h4>
              </div>

              {/* Description complète */}
              <div>
                <p className="text-lg text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {selectedActualite.description}
                </p>
              </div>

              {/* Informations supplémentaires */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">DATE</h4>
                  <div className="flex items-center gap-2 text-gray-900">
                    <Calendar className="w-5 h-5" />
                    <span className="text-lg">{formatDate(selectedActualite.date)}</span>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">CATÉGORIE</h4>
                  <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${getCategoryBadge(selectedActualite.category).class}`}>
                    {getCategoryBadge(selectedActualite.category).label}
                  </span>
                </div>

                {selectedActualite.isNew && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">STATUT</h4>
                    <span className="inline-block bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      🆕 Nouveau
                    </span>
                  </div>
                )}
              </div>

              {/* Bouton fermer */}
              <div className="flex justify-center pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowActualiteModal(false);
                    setSelectedActualite(null);
                  }}
                  className="px-8 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-lg font-medium"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Découvrir */}
      {showDecouvrirModal && selectedDecouvrir && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100000] p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
              <h3 className="text-2xl font-bold text-gray-900">Découvrir</h3>
              <button
                onClick={() => {
                  setShowDecouvrirModal(false);
                  setSelectedDecouvrir(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-8 h-8" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Vidéo si présente */}
              {selectedDecouvrir.video && (
                <div className="w-full rounded-lg overflow-hidden">
                  {selectedDecouvrir.video.includes('youtube.com') || selectedDecouvrir.video.includes('youtu.be') ? (
                    <iframe
                      src={selectedDecouvrir.video.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                      title={selectedDecouvrir.title}
                      className="w-full h-[400px]"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <video
                      src={selectedDecouvrir.video}
                      controls
                      className="w-full h-auto max-h-[400px] bg-black"
                    />
                  )}
                </div>
              )}

              {/* Galerie d'images */}
              <ModalGallery key={selectedDecouvrir.id} item={selectedDecouvrir} />

              {/* Titre */}
              <div>
                <h4 className="text-3xl font-bold text-gray-900">{selectedDecouvrir.title}</h4>
              </div>

              {/* Description complète */}
              <div>
                <p className="text-lg text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {selectedDecouvrir.description}
                </p>
              </div>

              {/* Informations supplémentaires */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">DATE</h4>
                  <div className="flex items-center gap-2 text-gray-900">
                    <Calendar className="w-5 h-5" />
                    <span className="text-lg">
                      {selectedDecouvrir.date ? formatDate(selectedDecouvrir.date) : 'Date non définie'}
                    </span>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">CATÉGORIE</h4>
                  <span className="inline-block px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-700">
                    {selectedDecouvrir.category || 'produit'}
                  </span>
                </div>
              </div>

              {/* Bouton fermer */}
              <div className="flex justify-center pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowDecouvrirModal(false);
                    setSelectedDecouvrir(null);
                  }}
                  className="px-8 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-lg font-medium"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Produit */}
      {showProductModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100000] p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
              <h3 className="text-2xl font-bold text-gray-900">Produit</h3>
              <button
                onClick={() => {
                  setShowProductModal(false);
                  setSelectedProduct(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-8 h-8" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Vidéo si présente */}
              {selectedProduct.video && (
                <div className="w-full rounded-lg overflow-hidden">
                  {selectedProduct.video.includes('youtube.com') || selectedProduct.video.includes('youtu.be') ? (
                    <iframe
                      src={selectedProduct.video.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                      title={selectedProduct.title}
                      className="w-full h-[400px]"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <video
                      src={selectedProduct.video}
                      controls
                      className="w-full h-auto max-h-[400px] bg-black"
                    />
                  )}
                </div>
              )}

              {/* Galerie d'images */}
              <ModalGallery key={selectedProduct.id} item={selectedProduct} />

              {/* Titre */}
              <div>
                <h4 className="text-3xl font-bold text-gray-900">{selectedProduct.title}</h4>
              </div>

              {/* Description complète */}
              <div>
                <p className="text-lg text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {selectedProduct.description}
                </p>
              </div>

              {/* Conseil */}
              {selectedProduct.cookingTimeOrAdvice && (
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                  <h4 className="text-sm font-medium text-green-800 mb-2">CONSEIL</h4>
                  <p className="text-green-700 font-medium">
                    {selectedProduct.cookingTimeOrAdvice}
                  </p>
                </div>
              )}

              {/* Bouton fermer */}
              <div className="flex justify-center pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowProductModal(false);
                    setSelectedProduct(null);
                  }}
                  className="px-8 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-lg font-medium"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox.isOpen && (
        <Lightbox
          images={lightbox.images}
          currentIndex={lightbox.currentIndex}
          onClose={lightbox.closeLightbox}
          onPrevious={lightbox.previousImage}
          onNext={lightbox.nextImage}
        />
      )}
    </main>
  );
};

export default Home;